import { startOfMonth, endOfMonth, subMonths, eachDayOfInterval, getDay, subDays, startOfDay, addMonths, addDays, isSameDay, isBefore, isAfter, isToday, startOfWeek, endOfWeek, Locale } from 'date-fns';
import { Inject, Injectable, Optional } from '@angular/core';

import { DAYS_OF_WEEKS, DaysName } from './utils/constants';

import { IDateWithMeta, IFilledMonth, ISelectedRange } from './utils/types';
import { dateFormatter } from '../../utils';
import { PhxTranslationService } from '../../translation/translation.service';
import { mapToDateFnsLocale } from './utils/helpers';
import { PHX_LOCALE_PROVIDER, PhxLocaleProvider } from '../locale/phx-locale.token';

const FIRST_DAY_OF_WEEK_INDEX = 0;
const LAST_DAY_OF_WEEK_INDEX = 6;

@Injectable({
  providedIn: 'root',
})
export class PhxDatePickerService {
  constructor(
    private translationService: PhxTranslationService,
    @Optional() @Inject(PHX_LOCALE_PROVIDER) private phxLocale: PhxLocaleProvider | null,
  ) { }

  /**
   * Returns an array of strings representing the days of the week, formatted according to the specified format string.
   *
   * @function
   * @param {number} [firstDay] - The index of the first day of the week (0 = Sunday, 1 = Monday, 2 = Tuesday, ..., 6 = Saturday). Defaults to the value from the injected `PHX_LOCALE_PROVIDER`, or 0 (Sunday) if no provider is available.
   * @param {string} [format='1'] - The format of the output strings.
   *        '1': returns the first letter of each day (e.g. 'S', 'M', 'T', ...)
   *        '3': returns the first three letters of each day (e.g. 'Sun', 'Mon', 'Tue', ...)
   *        'full': returns the full name of each day (e.g. 'Sunday', 'Monday', 'Tuesday', ...)
   * @returns {string[]} - An array of strings representing the days of the week.
   */
  public allDaysOfWeek(firstDay?: number, format?: string): string[] {
    const resolvedFirstDay = firstDay ?? this.getFirstDayOfWeek();
    const allDays = [];
    for (let i = 0; i < DAYS_OF_WEEKS.length; i++) {
      const day = DAYS_OF_WEEKS[(resolvedFirstDay + i) % 7];
      switch (format) {
        case '1':
          allDays.push(this.getDayOfWeek(i));
          break;
        case '3':
          allDays.push(this.getDayOfWeek(i, 'EEE'));
          break;
        case 'full':
          allDays.push(this.getDayOfWeek(i, 'EEEE'));
          break;
        default:
          allDays.push(this.getDayOfWeek(i));
          break;
      }
    }
    return allDays;
  }

 /**
  * Returns an object containing arrays of dates for the previous month, current month, and next month.
  *
  * @function
  * @param {Date} currentDate - The current date.
  * @returns {IFillMonth} An object with arrays of dates for the previous month, current month, and next month.
  */
  public getAllDayOfMonth(currentDate: Date): IFilledMonth {
    const weekStart = this.getFirstDayOfWeek();
    let dates: IFilledMonth = null;
    let currentDays: Date[] = [];
    const firstDayOfMonth: Date = startOfMonth(currentDate);
    const lastDayOfMonth: Date = endOfMonth(currentDate);
    const lastDayOfPrevMonth: Date = endOfMonth(subMonths(currentDate, 1));
    currentDays = eachDayOfInterval({
      start: firstDayOfMonth,
      end: lastDayOfMonth,
    });

    let previousDaysToFillGaps: Date[] = [];
    let monthStartDayIndex: number = (getDay(firstDayOfMonth) - weekStart + 7) % 7;
    const previousDateToFillGaps: Date = subDays(
      lastDayOfPrevMonth,
      monthStartDayIndex && monthStartDayIndex - 1
    );
    if (monthStartDayIndex > FIRST_DAY_OF_WEEK_INDEX) {
      previousDaysToFillGaps = eachDayOfInterval({
        start:  startOfDay(previousDateToFillGaps),
        end: subDays(firstDayOfMonth, 1),
      });
    }

    let nextDaysToFillGaps: Date[] = [];
    const dayNoAtEndOfCurrentMonth: number = (getDay(lastDayOfMonth) - weekStart + 7) % 7;
    const remaingDaysCountForTheMonth: number = LAST_DAY_OF_WEEK_INDEX - dayNoAtEndOfCurrentMonth;
    if (remaingDaysCountForTheMonth > FIRST_DAY_OF_WEEK_INDEX) {
      nextDaysToFillGaps = eachDayOfInterval({
        start: addMonths(firstDayOfMonth, 1),
        end: addDays(lastDayOfMonth, remaingDaysCountForTheMonth),
      });
    }
    dates = {
      prevMonth: previousDaysToFillGaps,
      currMonth: currentDays,
      nextMonth: nextDaysToFillGaps,
    };
    return dates;
  }

  /**
   * Transforms a CSS class name representing a selected date range into a class name that includes
   * additional modifiers based on the specific date within the range.
   * @param {string} className - The CSS class name representing the selected date range.
   * @param {IDateWithMeta} selectedDateWithMeta - The specific date within the selected range.
   * @returns {string} - The transformed CSS class name.
   */
  public transformToMultiSelection(
    className: string,
    selectedDateWithMeta: IDateWithMeta,
    selectedDateRange: ISelectedRange
  ): string {
    let subClassName = 'selected';
    if (selectedDateRange?.start && selectedDateRange?.end) {
      const isFrom = isSameDay(
        selectedDateWithMeta.date,
        selectedDateRange?.start
      );
      const isTo = isSameDay(
        selectedDateWithMeta.date,
        selectedDateRange.end
      );
      if (!(isFrom && isTo)) {
        if (isFrom) {
          subClassName += '-from';
        } else if (isTo) {
          subClassName += '-to';
        } else {
          subClassName += '-range';
        }
      }
    }
    return className.replace('selected', subClassName);
  }

  public getDatesInRange(from: Date, to: Date): Date[] {
    return eachDayOfInterval({
      start: from,
      end: to,
    });
  }

  public isSameAndBefore(date: Date, pastDate: Date) {
    return isSameDay(date, pastDate) || isBefore(date, pastDate);
  }

  public isSameAndAfter(date: Date, futureDate: Date) {
    return isSameDay(date, futureDate) || isAfter(date, futureDate);
  }

  public transformRangeToStartOfDayRange(range: ISelectedRange): ISelectedRange {
    return {
      ...(range?.start && { start: startOfDay(range?.start) }),
      ...(range?.end && { end: startOfDay(range?.end) }),
    };
  }

  public transformToStartOfDay(date: Date): Date {
    return startOfDay(date);
  }

  public isSameDate(dateLeft: Date, dateRight: Date): boolean {
    return isSameDay(dateLeft, dateRight);
  }

  public isBeforeDate(date: Date, dateToCompare: Date): boolean {
    return isBefore(startOfDay(date), startOfDay(dateToCompare));
  }

  public isAfterDate(date: Date, dateToCompare: Date): boolean {
    return isAfter(startOfDay(date), startOfDay(dateToCompare));
  }

  public isTodayDate(date: Date): boolean {
    return isToday(date);
  }

  public isDateInRange(date: Date, range: ISelectedRange): boolean {
    return isAfter(date, range.start) && isBefore(date, range.end);
  }

  public startOfWeekDate(date: Date, startDay: DaysName): Date {
    return addDays(startOfWeek(date), startDay);
  }

  public endOfWeekDate(date: Date, startDay: DaysName): Date {
    return addDays(endOfWeek(date), startDay);
  }

  public subNoOfDays(date: Date, noOfDays: number): Date {
    return subDays(date, noOfDays);
  }

  public addNoOfDays(date: Date, noOfDays: number): Date {
    return addDays(date, noOfDays);
  }

  public getDateFnsLocale(): Locale {
    return this.phxLocale
      ? this.phxLocale.getDateFnsLocale()
      : mapToDateFnsLocale(this.translationService.getLocale());
  }

  private getFirstDayOfWeek(): number {
    return this.phxLocale ? this.phxLocale.getFirstDayOfWeek() : 0;
  }

  private getDayOfWeek(day: number, format: string = "EEEEE"): string {
    const weekStartsOn = this.getFirstDayOfWeek() as 0 | 1 | 2 | 3 | 4 | 5 | 6;
    return dateFormatter(
      addDays(startOfWeek(new Date(), { weekStartsOn }), day),
      format,
      this.getDateFnsLocale(),
    );
  }
}
